import type { Page } from '@/components/canvas/document/publicTypes';
import { pageChromeWorldHeight } from '@/components/canvas/workspace/model/pageArrangement';

export interface WorldBounds {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface PageWorldBoundsOptions {
  includeChrome?: boolean;
}

/** 视口外扩边距（世界坐标），避免边缘闪烁 */
export const VIEWPORT_CULL_PADDING = 120;

/** 单页在世界坐标系下的包围盒（含标题栏） */
export function getPageWorldBounds(
  page: Page,
  zoom = 1,
  options?: PageWorldBoundsOptions,
): WorldBounds {
  const { x, y } = page.workspaceLayout;
  const chromeH = options?.includeChrome === false ? 0 : pageChromeWorldHeight(zoom);
  return {
    x,
    y: y - chromeH,
    width: page.size.width,
    height: page.size.height + chromeH,
  };
}

/** 当前 Workspace 视口对应的世界坐标矩形 */
export function getViewportWorldBounds(
  containerWidth: number,
  containerHeight: number,
  panX: number,
  panY: number,
  zoom: number,
): WorldBounds {
  return {
    x: -panX / zoom,
    y: -panY / zoom,
    width: containerWidth / zoom,
    height: containerHeight / zoom,
  };
}

export function expandWorldBounds(bounds: WorldBounds, padding: number): WorldBounds {
  return {
    x: bounds.x - padding,
    y: bounds.y - padding,
    width: bounds.width + padding * 2,
    height: bounds.height + padding * 2,
  };
}

export function boundsIntersect(a: WorldBounds, b: WorldBounds): boolean {
  return (
    a.x < b.x + b.width &&
    a.x + a.width > b.x &&
    a.y < b.y + b.height &&
    a.y + a.height > b.y
  );
}

/** 所有 Page 在世界坐标系下的轴对齐包围盒 */
export function getPagesWorldBounds(pages: Page[], zoom = 1): WorldBounds | null {
  if (pages.length === 0) return null;
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const page of pages) {
    const b = getPageWorldBounds(page, zoom);
    minX = Math.min(minX, b.x);
    minY = Math.min(minY, b.y);
    maxX = Math.max(maxX, b.x + b.width);
    maxY = Math.max(maxY, b.y + b.height);
  }
  const width = maxX - minX;
  const height = maxY - minY;
  if (width <= 0 || height <= 0) return null;
  return { x: minX, y: minY, width, height };
}

/** 返回与视口相交的 pageId 集合 */
export function getVisiblePageIds(
  pages: Page[],
  viewport: WorldBounds,
  padding = VIEWPORT_CULL_PADDING,
  zoom = 1,
): Set<string> {
  const expanded = expandWorldBounds(viewport, padding);
  const ids = new Set<string>();
  for (const page of pages) {
    if (boundsIntersect(getPageWorldBounds(page, zoom), expanded)) {
      ids.add(page.id);
    }
  }
  return ids;
}
